import { useContext, useMemo, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  OptionSetsDropdown,
  SitesDropdown,
  StorageLocationAutoComplete,
  WithLocalStorage,
} from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import {
  StorageLocationCombobox,
  type StorageLocationOption,
} from '@/components/common/storage-location-combobox';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FormField } from '@/pages/embryo-details/components/FormField';
import { useMergeValueIntoOptions } from '@/pages/embryo-details/hooks/useMergeValueIntoOptions';
import { PrimaryContext, queryClient } from '@/services';
import {
  EmbryoDetailDto,
  FreezeEntryDto,
  useGetFreezeEntries,
} from '@/services/embryo-service';
import { embryoServiceApi } from '@/services/embryo-service/api';
import { useContextMenu } from '@/utils/context-menu';
import { runNewWorkflow, WORKFLOW } from '@/utils/workflow';

import { freezeEntryColumnDefs } from '../../util/columns/freeze-entries';
import { AddMissingStrawsDialog } from '../AddMissingStrawsDialog';
import { EditFreezeEntriesDialog } from '../EditFreezeEntriesDialog';
import {
  EMBRYO_STATUS,
  OUTSIDE_EMBRYO_FREEZING_FIELDS,
  REVERSAL_REQUIRED_FIELDS,
} from './constants';
import { EmbryoFormFieldConfig } from './field-config';
import {
  exportOptions,
  useAccountOptions,
  useTechOptions,
} from './form-options';
import { useEmbryoFieldPolicy, useEmbryoFieldsEnabled } from './full-inventory';

type EmbryoFreezingTabProps = {
  control: Control<EmbryoDetailDto>;
  watch: UseFormWatch<EmbryoDetailDto>;
  setValue: UseFormSetValue<EmbryoDetailDto>;
  isBulkMode: boolean;
  columns?: number;
  isOutsideEmbryo?: boolean;
  outsideEmbryoId?: string;
};

export function EmbryoFreezingTab({
  control,
  watch,
  setValue,
  isBulkMode: _isBulkMode,
  columns = 3,
  isOutsideEmbryo = false,
  outsideEmbryoId,
}: EmbryoFreezingTabProps) {
  const dispatch = useAppDispatch();
  const defaultGridOptions = useDefaultGridOptions();
  const addRemoveEmbryoFields = useEmbryoFieldsEnabled();
  const isAccountComboboxEnabled = useFeatureFlag(
    'Account_Combobox_not_Dropdown',
  );
  const policy = useEmbryoFieldPolicy({ isOutsideEmbryo });
  const isOutsideEmbryoUpdatesEnabled = useFeatureFlag(
    'Outside_Embryo_Updates',
  );
  const isCreateEmbryoButtonRemoved = useFeatureFlag(
    'Remove_Create_Embryo_Button',
  );
  const isStorageLocationPagingEnabled = useFeatureFlag(
    'Storage_Location_Paging',
  );

  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);

  const gridRef = useRef<AgGridReact>(null);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const [selectedRows, setSelectedRows] = useState<FreezeEntryDto[]>([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [addMissingStrawsDialogVisible, setAddMissingStrawsDialogVisible] =
    useState(false);
  const status = watch('status');
  const freshFrozen = watch('freshFrozen');
  const storageLocationId = watch('storageLocationId');
  const storageLocationName = watch('storageLocationName');
  const { data: freezeEntriesResult, isLoading: _isFreezeEntriesLoading } =
    useGetFreezeEntries(outsideEmbryoId);

  const _siteId = watch('siteId');

  const techsOptions = useTechOptions();
  const accountOptions = useAccountOptions();
  const watchAny: UseFormWatch<EmbryoDetailDto> = watch;

  const freezeOwnerOptions = useMergeValueIntoOptions({
    currentOption: {
      label: watchAny('freezeOwnerName') ?? '',
      value: watchAny('freezeOwnerId') ?? '',
    },
    options: accountOptions,
  });

  const isRequired = (fieldName: string) =>
    status === EMBRYO_STATUS.FROZEN &&
    (REVERSAL_REQUIRED_FIELDS as readonly string[]).includes(fieldName);

  const fieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'freezeOwnerId',
        label: t('freeze.freezeOwner'),
        component: isAccountComboboxEnabled ? AccountsCombobox : Dropdown,
        ...(!isAccountComboboxEnabled && {
          lockedDisplayField: 'freezeOwnerName',
        }),
        outsideOnly: true,
        componentProps: isAccountComboboxEnabled
          ? {}
          : {
              options: freezeOwnerOptions,
              placeholder: t('common.selectOption'),
            },
      },
      {
        name: 'freezeTechId',
        label: t('freeze.freezeTech'),
        component: Dropdown,
        componentProps: {
          options: techsOptions,
          placeholder: t('freeze.selectFreezeTech'),
        },
      },
      {
        name: 'recoveryFirmCode',
        label: t('freeze.recoveryFirmCode'),
        component: InputText,
      },
      {
        name: 'cane',
        label: t('semen.cane'),
        component: InputText,
        ...(addRemoveEmbryoFields && freshFrozen === 1 && { required: true }),
      },
      {
        name: 'stage',
        label: t('embryo.stage'),
        component: InputNumber,
      },
      { name: 'grade', label: t('embryo.grade'), component: InputNumber },
      {
        name: 'freezeTime',
        label: t('freeze.freezeTime'),
        component: InputText,
      },
      {
        name: 'freezeMethodId',
        label: t('freeze.freezeMethod'),
        component: OptionSetsDropdown,
        ...(addRemoveEmbryoFields && freshFrozen === 1 && { required: true }),
        componentProps: {
          optionSetsName: 'Freezing Methods',
          placeholder: t('freeze.selectFreezeMethod'),
        },
      },
      {
        name: 'storageLocationId',
        label: t('storageLocation.storageLocation'),
        component: isStorageLocationPagingEnabled
          ? StorageLocationCombobox
          : StorageLocationAutoComplete,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: isStorageLocationPagingEnabled
          ? {
              value:
                storageLocationId && storageLocationName
                  ? {
                      id: storageLocationId,
                      name: storageLocationName,
                    }
                  : null,
              onChange: (location: StorageLocationOption | null) => {
                setValue('storageLocationId', location?.id ?? null, {
                  shouldDirty: true,
                });
                setValue('storageLocationName', location?.name ?? null);
              },
              pagingEnabled: true,
              placeholder: t('common.selectOption'),
            }
          : {
              field: 'name',
              placeholder: t('common.selectOption'),
              defaultValue:
                storageLocationId && storageLocationName
                  ? {
                      id: storageLocationId,
                      name: storageLocationName,
                    }
                  : null,
            },
      },
      { name: 'freezer', label: t('freeze.freezer'), component: InputText },
      { name: 'dtPlus', label: t('freeze.dtPlus'), component: Checkbox },
      {
        name: 'creation',
        label: t('freeze.creationFreezeDate'),
        component: Calendar,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'receivedOn',
        label: t('common.received'),
        component: Calendar,
        ...(addRemoveEmbryoFields && freshFrozen === 1 && { required: true }),
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'tt',
        label: t('freeze.trypsinTreated'),
        component: Checkbox,
      },
      {
        name: 'grader7Id',
        label: t('grading.grader'),
        component: Dropdown,
        componentProps: {
          options: techsOptions,
          placeholder: t('grading.selectGrader'),
        },
      },
      {
        name: 'freezeComment',
        label: t('freeze.freezeComment'),
        component: InputText,
      },
      {
        name: 'crackedZona',
        label: t('embryo.crackedZona'),
        component: Checkbox,
      },
      {
        name: 'export',
        label: t('export.export'),
        component: Dropdown,
        componentProps: {
          options: exportOptions,
          placeholder: t('common.select'),
        },
      },
      {
        name: 'siteId',
        label: t('site.siteId'),
        component: SitesDropdown,
        ...(addRemoveEmbryoFields && { required: true }),
        componentProps: {
          placeholder: t('site.selectSite'),
        },
      },
      ...(addRemoveEmbryoFields
        ? ([
            {
              name: 'grader65',
              label: t('grading.graderDay65'),
              component: InputText,
            },
            {
              name: 'storageLocationValidationId',
              label: t('storageLocation.storageLocationValidation'),
              component: StorageLocationAutoComplete,
              componentProps: {
                field: 'name',
                placeholder: t('common.selectOption'),
              },
            },
            {
              name: 'label1',
              label: t('embryo.labelLine1'),
              component: InputText,
            },
            {
              name: 'label2',
              label: t('embryo.labelLine2'),
              component: InputText,
            },
            {
              name: 'flshId',
              label: t('embryo.flshId'),
              component: InputText,
            },
          ] satisfies EmbryoFormFieldConfig[])
        : []),
    ],
    [
      t,
      addRemoveEmbryoFields,
      isAccountComboboxEnabled,
      freshFrozen,
      freezeOwnerOptions,
      techsOptions,
      storageLocationId,
      storageLocationName,
      isStorageLocationPagingEnabled,
      setValue,
    ],
  );

  const lockedFields = ['creation'];

  const visibleFields = (
    isOutsideEmbryo
      ? fieldConfigs.filter((f) =>
          (OUTSIDE_EMBRYO_FREEZING_FIELDS as readonly string[]).includes(
            f.name,
          ),
        )
      : fieldConfigs.filter((f) => !f.outsideOnly)
  ).filter((f) => policy.isVisible(f.name));

  const handleAddStrawsConfirm = async (
    entries: { count: number; stage?: number; grade?: number }[],
  ) => {
    setAddMissingStrawsDialogVisible(false);

    try {
      const res = await embryoServiceApi.addStrawsV2({
        outsideEmbryoId,
        entries,
      });

      addToast({
        severity: 'success',
        summary: 'Straws Added',
        detail: 'Straws successfully added to outside embryo.',
      });

      if (isOutsideEmbryoUpdatesEnabled) {
        const freezeIds: string[] = res?.createdFreezeEntryIds ?? [];
        if (freezeIds.length > 0) {
          runNewWorkflow(
            freezeIds.map((id: string) => ({ RecordId: id })),
            WORKFLOW.CreateEmbryoFreeze.id,
          )
            .then(() => {
              setTimeout(() => {
                queryClient.invalidateQueries({
                  queryKey: ['embryo', 'freeze-entries', outsideEmbryoId],
                });
              }, 7000);
            })
            .catch(() => {
              addToast({
                severity: 'warn',
                summary: 'Workflow Warning',
                detail:
                  'Straws added but Create Embryo workflow failed to start.',
              });
            });
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ['embryo', 'freeze-entries', outsideEmbryoId],
      });
    } catch (error) {
      addToast({
        severity: 'error',
        summary: 'Error',
        detail:
          error instanceof Error ? error.message : 'Failed to add straws.',
      });
    }
  };

  const handleCreateEmbryo = async () => {
    const recordIds = selectedRows.map((row) => ({ RecordId: row.id }));

    try {
      await runNewWorkflow(recordIds, WORKFLOW.CreateEmbryoFreeze.id);

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['embryo', 'freeze-entries', outsideEmbryoId],
        });
      }, 7000);

      dispatch(
        addToast({
          severity: 'success',
          summary: 'Workflow Executed',
          detail:
            'The Create Embryo workflow has been started. The grid will refresh automatically in a few seconds.',
          life: ToastLife.LONG,
        }),
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Workflow Error',
          detail: `Failed to execute Create Embryo workflow. Please try again. Error: ${errorMessage}`,
          life: ToastLife.LONG,
        }),
      );
    }
  };

  return (
    <div className="embryo-freezing-tab">
      {!isOutsideEmbryo && (
        <Fieldset
          pt={{
            root: { className: 'p-2' },
          }}
        >
          <div
            className="grid gap-x-6 gap-y-1"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {visibleFields.map((field) => (
              <FormField
                key={field.name}
                label={field.label}
                name={field.name}
                control={control}
                component={field.component}
                disabled={
                  field.disabled ||
                  lockedFields.includes(field.name) ||
                  policy.isLocked(field.name)
                }
                required={policy.isRequired(
                  field.name,
                  field.required ?? isRequired(field.name),
                )}
                componentProps={field.componentProps}
              />
            ))}
          </div>
        </Fieldset>
      )}
      {isOutsideEmbryo && outsideEmbryoId && (
        <div className="mt-4">
          <div className="mb-2 flex justify-end gap-2">
            <>
              <Button
                label="Create Embryo"
                size="small"
                disabled={selectedRows.length === 0}
                onClick={handleCreateEmbryo}
                aria-label="Create Embryo"
              />
              <Button
                label="Refresh"
                size="small"
                icon="pi pi-refresh"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ['embryo', 'freeze-entries', outsideEmbryoId],
                  })
                }
                aria-label="Refresh Grid"
              />
            </>
            <Button
              label="Add Missing Straws"
              size="small"
              onClick={() => setAddMissingStrawsDialogVisible(true)}
            />
            <Button
              label="Edit Selected"
              size="small"
              disabled={selectedRows.length === 0}
              onClick={() => setEditDialogVisible(true)}
              tooltip={
                isOutsideEmbryoUpdatesEnabled
                  ? 'Editing will also update the associated live embryo'
                  : undefined
              }
              tooltipOptions={{ position: 'top' }}
            />
          </div>
          <div className="ag-grid-height-300">
            <WithLocalStorage storageKey="embryo-freeze-entries-grid">
              <AgGridReact
                gridOptions={defaultGridOptions}
                ref={gridRef}
                className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
                defaultColDef={defaultColDef}
                rowData={freezeEntriesResult?.items ?? []}
                columnDefs={freezeEntryColumnDefs}
                rowSelection="multiple"
                onSelectionChanged={() =>
                  setSelectedRows(gridRef.current!.api.getSelectedRows())
                }
                getContextMenuItems={getContextMenuItems}
              />
            </WithLocalStorage>
          </div>
        </div>
      )}
      <EditFreezeEntriesDialog
        visible={editDialogVisible}
        selectedRows={selectedRows}
        onHide={() => setEditDialogVisible(false)}
        showEmbryoUpdateNote={isOutsideEmbryoUpdatesEnabled}
      />
      <AddMissingStrawsDialog
        visible={addMissingStrawsDialogVisible}
        onHide={() => setAddMissingStrawsDialogVisible(false)}
        onConfirm={(entries) => {
          setAddMissingStrawsDialogVisible(false);
          handleAddStrawsConfirm(entries);
        }}
      />
    </div>
  );
}
